import { useState } from "react";
import {
  Plus,
  Bell,
  Trash2,
  Loader2,
  Mail,
  MessageSquare,
  Globe,
  Pencil,
  Send,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useNotificationConfigs,
  useUpdateNotificationConfig,
  useDeleteNotificationConfig,
  useTestNotificationConfig,
} from "@/hooks/use-api";
import type { NotificationConfig, TestNotificationResponse } from "@/types/api";
import { AddNotificationDialog } from "./add-notification-dialog";
import { EditNotificationDialog } from "./edit-notification-dialog";

const channelIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  email: Mail,
  slack: MessageSquare,
  webhook: Globe,
};

type TestResult =
  | { state: "idle" }
  | { state: "pending" }
  | { state: "done"; response: TestNotificationResponse };

export function NotificationsTab() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<NotificationConfig | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});

  const { data, isLoading, isError } = useNotificationConfigs();
  const updateMutation = useUpdateNotificationConfig();
  const deleteMutation = useDeleteNotificationConfig();
  const testMutation = useTestNotificationConfig();

  const configs = data?.configs ?? [];

  function handleToggle(configId: string, enabled: boolean) {
    updateMutation.mutate({ configId, enabled });
  }

  function handleDelete(configId: string) {
    if (!window.confirm("Delete this notification channel? This cannot be undone.")) return;
    setDeletingId(configId);
    deleteMutation.mutate(configId, {
      onSuccess: () => {
        // Prune any stale test-result entry for the just-deleted config so
        // the inline result panel doesn't reappear if a new config is later
        // created with the same id (or if the result map grows unbounded
        // over a long Settings session).
        setTestResults((prev) => {
          if (!(configId in prev)) return prev;
          const next = { ...prev };
          delete next[configId];
          return next;
        });
      },
      onSettled: () => setDeletingId(null),
    });
  }

  function handleTest(configId: string) {
    setTestResults((prev) => ({ ...prev, [configId]: { state: "pending" } }));
    testMutation.mutate(configId, {
      onSuccess: (response) => {
        setTestResults((prev) => ({
          ...prev,
          [configId]: { state: "done", response },
        }));
      },
      onError: (err) => {
        setTestResults((prev) => ({
          ...prev,
          [configId]: {
            state: "done",
            response: {
              status: "failed",
              error: err instanceof Error ? err.message : "Test failed",
              channel: "",
              latency_ms: 0,
            },
          },
        }));
      },
    });
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="mt-2 h-4 w-48" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          Notification Channels
        </h3>
        <Button size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add
        </Button>
      </div>

      {isError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load notification configs.
        </div>
      )}

      {configs.length === 0 && !isError && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <div className="rounded-full bg-muted p-3">
            <Bell className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-sm font-semibold">
            No notification channels configured
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Add a channel to receive alert notifications.
          </p>
          <Button
            className="mt-4"
            variant="outline"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add Channel
          </Button>
        </div>
      )}

      {configs.map((config) => {
        const Icon = channelIcons[config.channel] ?? Bell;
        const configDetails = getConfigDetails(config.channel, config.config);
        const testResult = testResults[config.id] ?? { state: "idle" as const };
        const cats = config.category_filter ?? [];

        return (
          <Card key={config.id}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-primary/10 p-2">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium capitalize">{config.channel}</p>
                    <Badge variant="secondary" className="text-xs">
                      Min: {config.min_severity}
                    </Badge>
                    {cats.length === 0 ? (
                      <Badge variant="outline" className="text-xs">
                        All categories
                      </Badge>
                    ) : (
                      cats.map((c) => (
                        <Badge key={c} variant="outline" className="text-xs">
                          {c}
                        </Badge>
                      ))
                    )}
                  </div>
                  <p className="truncate text-sm text-muted-foreground">
                    {configDetails}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={(checked: boolean) =>
                      handleToggle(config.id, checked)
                    }
                    aria-label={`Enable ${config.channel} notifications`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleTest(config.id)}
                    disabled={testResult.state === "pending" || !config.enabled}
                    aria-label={
                      config.enabled
                        ? `Send test ${config.channel} notification`
                        : `Send test ${config.channel} notification (channel disabled)`
                    }
                    title={!config.enabled ? "Enable this channel to send a test" : undefined}
                  >
                    {testResult.state === "pending" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditTarget(config)}
                    aria-label={`Edit ${config.channel} notification channel`}
                  >
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(config.id)}
                    disabled={deletingId === config.id}
                    aria-label={`Delete ${config.channel} notification channel`}
                  >
                    {deletingId === config.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              {testResult.state === "done" && (
                <div
                  role="status"
                  className={
                    "mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs " +
                    (testResult.response.status === "sent"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "border-destructive/30 bg-destructive/10 text-destructive")
                  }
                >
                  {testResult.response.status === "sent" ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 shrink-0" />
                  )}
                  <span className="min-w-0">
                    {testResult.response.status === "sent"
                      ? `Test notification sent (${testResult.response.latency_ms} ms). Check your channel.`
                      : `Test failed: ${testResult.response.error ?? "unknown error"}`}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <AddNotificationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
      <EditNotificationDialog
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
        config={editTarget}
      />
    </div>
  );
}

function getConfigDetails(
  channel: string,
  config: Record<string, unknown>,
): string {
  switch (channel) {
    case "email": {
      const recipients = config.recipients;
      if (Array.isArray(recipients)) return recipients.join(", ");
      if (typeof recipients === "string") return recipients;
      return "Email notifications";
    }
    case "slack": {
      const ch = config.channel ?? config.webhook_url;
      return typeof ch === "string" ? ch : "Slack notifications";
    }
    case "webhook": {
      const url = config.url ?? config.webhook_url;
      return typeof url === "string" ? url : "Webhook notifications";
    }
    default:
      return "Notifications";
  }
}
